/**
 * FileDetailPage — Shows file (transmission) metadata, lifecycle, and payment list.
 * Uses the GET /file/{fileId} API response model.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { FileLifecycleState } from 'src/store/slices/fileDetail/fileDetailState';
import { FILE_DETAIL_LABELS } from './fileDetailConstants';
import { formatTimestamp, formatLatency } from 'src/utils/formatters';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { fetchFileDetail, clearFileDetail } from 'src/store/slices/fileDetail/fileDetailSlice';
import { addNotification, clearNotifications } from 'src/store/slices/notifications/notificationSlice';
import { CopyableText } from 'src/components/common/CopyableText';
import { LifecycleTracker, LifecycleStage, StepStatus } from 'src/components/common/LifecycleView';

const stateToStatusType = (state: FileLifecycleState) => {
  if (state === 'SUCCESS') return 'success' as const;
  if (state === 'FAILED') return 'error' as const;
  return 'in-progress' as const;
};

const mapState = (state: string): StepStatus => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  return 'loading';
};

export const FileDetailPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const decodedName = searchParams.get('name') ?? '';

  const { data, status, error } = useAppSelector((state) => state.fileDetail);

  useEffect(() => {
    if (decodedName) {
      dispatch(clearNotifications());
      dispatch(fetchFileDetail(decodedName));
    }
    return () => { dispatch(clearFileDetail()); };
  }, [decodedName, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearNotifications());
      dispatch(addNotification({ type: 'error', header: 'Failed to load file', content: error }));
    }
  }, [error, dispatch]);

  const handleRefresh = useCallback(() => {
    if (decodedName) dispatch(fetchFileDetail(decodedName));
  }, [decodedName, dispatch]);

  const breadcrumbs = useMemo(
    () => [
      { text: 'Search', href: '/' },
      { text: decodedName || 'File', href: '#' },
    ],
    [decodedName],
  );

  // Error state — show error with retry
  if (status === 'failed') {
    return (
      <div style={{ padding: 24 }}>
        <SpaceBetween size="l">
          <BreadcrumbGroup
            items={breadcrumbs}
            onFollow={(e) => { e.preventDefault(); if (e.detail.href !== '#') navigate(e.detail.href); }}
          />
          <Container>
            <Box textAlign="center" padding={{ vertical: 'xl' }}>
              <SpaceBetween size="s" alignItems="center">
                <StatusIndicator type="error">
                  {error || 'Failed to load file details.'}
                </StatusIndicator>
                <Button onClick={handleRefresh}>Retry</Button>
              </SpaceBetween>
            </Box>
          </Container>
        </SpaceBetween>
      </div>
    );
  }

  // Loading state
  if (status === 'loading' || !data) {
    return (
      <div style={{ padding: 24 }}>
        <SpaceBetween size="l">
          <BreadcrumbGroup
            items={breadcrumbs}
            onFollow={(e) => { e.preventDefault(); if (e.detail.href !== '#') navigate(e.detail.href); }}
          />
          <Container>
            <Box textAlign="center" padding={{ vertical: 'xl' }}>
              <SpaceBetween size="s" alignItems="center">
                <Spinner size="large" />
                <Box variant="p" color="text-body-secondary">
                  {decodedName ? `Loading file ${decodedName}…` : 'Loading file details…'}
                </Box>
              </SpaceBetween>
            </Box>
          </Container>
        </SpaceBetween>
      </div>
    );
  }

  // Build lifecycle stages from statusMap
  const stages: LifecycleStage[] = Object.entries(data.statusMap || {}).map(([name, detail]) => ({
    name,
    status: mapState(detail.state),
    isReached: true,
    timestamp: detail.enteredAt ? new Date(detail.enteredAt * 1000).toISOString() : undefined,
  }));

  // Build detail renderer for lifecycle
  const renderStageDetail = (stageName: string) => {
    const detail = data.statusMap?.[stageName];
    if (!detail) return null;
    const items: { label: string; value: string }[] = [
      { label: 'Status', value: detail.paymentStatus || '—' },
      { label: 'State', value: detail.state },
      { label: 'Latency', value: formatLatency(detail.latencyMs) },
    ];
    if (detail.attributes) {
      Object.entries(detail.attributes).forEach(([key, value]) => {
        if (value) items.push({ label: key, value });
      });
    }
    return (
      <KeyValuePairs columns={2} items={items.map(i => ({ label: i.label, value: i.value }))} />
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <SpaceBetween size="l">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BreadcrumbGroup
            items={breadcrumbs}
            onFollow={(e) => { e.preventDefault(); if (e.detail.href !== '#') navigate(e.detail.href); }}
          />
          <Button iconName="refresh" onClick={handleRefresh}>
            Refresh
          </Button>
        </div>

        {/* File metadata card */}
        <Container
          header={
            <Header variant="h2">
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <CopyableText value={data.id} />
                <StatusIndicator type={stateToStatusType(data.beaconTransmissionState)}>
                  {data.beaconTransmissionState}
                </StatusIndicator>
              </SpaceBetween>
            </Header>
          }
        >
          <KeyValuePairs
            columns={4}
            items={[
              { label: FILE_DETAIL_LABELS.fileType, value: data.fileType ? <Badge>{data.fileType}</Badge> : '—' },
              {
                label: FILE_DETAIL_LABELS.direction,
                value: <Badge color={data.direction === 'OUTBOUND' ? 'blue' : 'grey'}>{data.direction}</Badge>,
              },
              { label: FILE_DETAIL_LABELS.bank, value: data.bank || '—' },
              { label: FILE_DETAIL_LABELS.dataSource, value: <Badge>{data.dataSource}</Badge> },
              { label: FILE_DETAIL_LABELS.createdAt, value: formatTimestamp(data.createdAt) },
              { label: FILE_DETAIL_LABELS.updatedAt, value: formatTimestamp(data.updatedAt) },
              { label: FILE_DETAIL_LABELS.totalE2E, value: formatLatency(data.totalE2ELatencyMs) },
              { label: FILE_DETAIL_LABELS.paymentCount, value: String(data.paymentIds?.length || 0) },
            ]}
          />
        </Container>

        {/* Lifecycle */}
        {stages.length > 0 && (
          <LifecycleTracker
            title="File Lifecycle"
            stages={stages}
            renderDetail={renderStageDetail}
          />
        )}
      </SpaceBetween>
    </div>
  );
};
